import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../../services/api';
import {
  FiLayers, FiSearch, FiRefreshCw,
  FiCheckCircle, FiClock, FiXCircle, FiZap, FiAlertCircle,
} from 'react-icons/fi';

function CategoryTableRow({ row, maxTotal, totalQuotes }) {
  const isQuick = row.isQuickServiceRow === true;
  return (
    <tr
      className={`border-b border-gray-800/80 transition-colors ${
        isQuick
          ? 'bg-purple-950/30 hover:bg-purple-950/50'
          : 'hover:bg-gray-900/40'
      }`}
    >
      <td className="px-6 py-4">
        <div className="flex items-center gap-3">
          <span
            className="w-3 h-3 rounded-full shrink-0"
            style={{ backgroundColor: row.color }}
          />
          <span className={`font-semibold ${isQuick ? 'text-purple-200' : 'text-white'}`}>
            {isQuick && <FiZap className="inline w-4 h-4 mr-1.5 text-purple-400" />}
            {row.label}
          </span>
        </div>
      </td>
      <td className="px-4 py-4 font-mono text-xs text-gray-500">{row.serviceType}</td>
      <td className="px-4 py-4 text-right font-bold text-yellow-400">{row.total}</td>
      <td className="px-4 py-4 text-right text-yellow-300">
        <span className="inline-flex items-center justify-end gap-1">
          <FiClock className="w-3.5 h-3.5" />
          {row.pending}
        </span>
      </td>
      <td className="px-4 py-4 text-right text-blue-300">{row.accepted}</td>
      <td className="px-4 py-4 text-right text-indigo-300">{row.activeAfterAccept ?? row.inProgress}</td>
      <td className="px-4 py-4 text-right text-green-400">
        <span className="inline-flex items-center justify-end gap-1">
          <FiCheckCircle className="w-3.5 h-3.5" />
          {row.completed}
        </span>
      </td>
      <td className="px-4 py-4 text-right text-red-300">
        <span className="inline-flex items-center justify-end gap-1">
          <FiXCircle className="w-3.5 h-3.5" />
          {row.cancelled}
        </span>
      </td>
      <td className="px-6 py-4">
        <div className="flex items-center gap-2">
          <div className="flex-1 h-2 bg-gray-800 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{
                width: `${Math.round((row.total / maxTotal) * 100)}%`,
                backgroundColor: row.color,
              }}
            />
          </div>
          <span className="text-xs text-gray-500 w-10 text-right">
            {totalQuotes > 0 ? `${Math.round((row.total / totalQuotes) * 100)}%` : '0%'}
          </span>
        </div>
      </td>
    </tr>
  );
}

function AdminCategories() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({
    totalTypes: 0,
    totalQuotes: 0,
    pending: 0,
    completed: 0,
    inProgress: 0,
    quickServiceCount: 0,
    quickService: { total: 0, cancelled: 0, active: 0, pending: 0, completed: 0 },
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getAllCategories();
      if (response?.success) {
        setRows(response.data || []);
        if (response.stats) setStats(response.stats);
      } else {
        throw new Error(response?.message || 'Erreur lors du chargement');
      }
    } catch (err) {
      console.error('Types de service:', err);
      setError(err.message || 'Impossible de charger les statistiques.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filtered = useMemo(() => {
    const q = searchTerm.toLowerCase().trim();
    if (!q) return rows;
    return rows.filter(
      (row) =>
        row.label?.toLowerCase().includes(q) ||
        row.serviceType?.toLowerCase().includes(q) ||
        (q.includes('rapide') && row.isQuickServiceRow)
    );
  }, [rows, searchTerm]);

  const maxTotal = Math.max(...filtered.map((r) => r.total), 1);

  return (
    <div className="space-y-6 p-6 bg-gray-900 min-h-screen text-gray-100">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center pb-4 border-b border-yellow-600/30">
        <div>
          <h1 className="text-4xl font-extrabold text-yellow-400 mb-2 flex items-center">
            <FiLayers className="w-8 h-8 mr-3" />
            Types de service
          </h1>
          <p className="text-gray-400">
            Statistiques réelles issues des demandes (<code className="text-yellow-500/80">quotes.services</code>
            ) — lecture seule.
          </p>
        </div>
        <button
          type="button"
          onClick={loadData}
          disabled={loading}
          className="mt-4 sm:mt-0 px-5 py-2.5 bg-yellow-600 text-gray-900 rounded-lg hover:bg-yellow-500 transition-colors font-semibold flex items-center shadow-lg disabled:opacity-50"
        >
          <FiRefreshCw className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </button>
      </div>

      {error && (
        <div className="bg-red-900/30 border-l-4 border-red-600 rounded-lg p-4 flex items-center gap-3">
          <FiAlertCircle className="h-5 w-5 text-red-400 shrink-0" />
          <p className="text-sm text-red-300 flex-1">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl p-5">
          <p className="text-xs font-semibold text-gray-400 uppercase mb-1">Types distincts</p>
          <p className="text-3xl font-bold text-yellow-400">{stats.totalTypes}</p>
        </div>
        <div className="bg-gradient-to-br from-blue-600/10 to-gray-900 border border-blue-600/30 rounded-xl p-5">
          <p className="text-xs font-semibold text-gray-400 uppercase mb-1">Total demandes</p>
          <p className="text-3xl font-bold text-blue-400">{stats.totalQuotes}</p>
        </div>
        <div className="bg-gradient-to-br from-green-600/10 to-gray-900 border border-green-600/30 rounded-xl p-5">
          <p className="text-xs font-semibold text-gray-400 uppercase mb-1">Terminées</p>
          <p className="text-3xl font-bold text-green-400">{stats.completed}</p>
        </div>
        <div className="bg-gradient-to-br from-purple-600/10 to-gray-900 border border-purple-600/30 rounded-xl p-5">
          <p className="text-xs font-semibold text-gray-400 uppercase mb-1">Transport rapide</p>
          <p className="text-3xl font-bold text-purple-400 flex items-center gap-2">
            <FiZap className="w-6 h-6" />
            {stats.quickService?.total ?? stats.quickServiceCount}
          </p>
          <p className="text-xs text-gray-500 mt-2">
            ligne dédiée dans le tableau ci-dessous
          </p>
        </div>
      </div>

      <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl p-4">
        <div className="relative">
          <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher un type (libellé ou code)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-gray-900/50 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-yellow-500"
          />
        </div>
      </div>

      <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl overflow-hidden shadow-xl">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-14 w-14 border-t-4 border-b-4 border-yellow-500" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-400">Aucun type trouvé</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-700 bg-gray-900/60 text-gray-400 uppercase text-xs">
                  <th className="px-6 py-4 font-semibold">Type</th>
                  <th className="px-4 py-4 font-semibold">Code</th>
                  <th className="px-4 py-4 font-semibold text-right">Total</th>
                  <th className="px-4 py-4 font-semibold text-right">En attente</th>
                  <th className="px-4 py-4 font-semibold text-right">Négo.</th>
                  <th className="px-4 py-4 font-semibold text-right" title="Accepté ou en cours (transport rapide = in_progress)">
                    En cours / accepté
                  </th>
                  <th className="px-4 py-4 font-semibold text-right">Terminées</th>
                  <th className="px-4 py-4 font-semibold text-right">Annulées</th>
                  <th className="px-6 py-4 font-semibold min-w-[140px]">Répartition</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <CategoryTableRow
                    key={row.serviceType}
                    row={row}
                    maxTotal={maxTotal}
                    totalQuotes={stats.totalQuotes}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="text-xs text-gray-500 text-center pb-4 max-w-3xl mx-auto">
        Types lus depuis <strong>services.serviceType</strong> ou <strong>[service: …]</strong>. La ligne{' '}
        <strong>Transport rapide</strong> regroupe les demandes avec <strong>isQuickService</strong> (hors des
        autres lignes). À l’acceptation, statut <strong>in_progress</strong>.
      </p>
    </div>
  );
}

export default AdminCategories;
